import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { BomStatusSchema, CostBreakdownSchema, MfgPctSchema, } from './manufacturing.js';
import { pagedQuery, pagedResponse } from './pagination.js';
import { MessageSchema, QtyStringSchema, UnitPrecisionSchema } from './primitives.js';
const c = initContract();
/**
 * MFG-01 — BILLS OF MATERIALS. The recipe for something you make.
 *
 * "You cannot type a cost here. You can only type the recipe, and the money
 * follows" (§1). Every figure in `cost` comes from the shared costing service
 * (§9.4) and no request on this contract carries one.
 *
 * ── The lifecycle, which is the whole screen ────────────────────────────────
 *   Draft    freely editable and freely deletable (BR-MFG-01-18, ruling R8)
 *   Active   read-only APART FROM the overhead percentage (BR-MFG-01-13)
 *   Inactive read-only; may be re-activated
 *
 * OQ-9.7-4 answers the question the prototype leaves open — there is no "New
 * version" button anywhere on the screen, so version creation must happen by
 * itself: "Activating a version freezes it and opens a new Draft one number
 * higher, copied from it." That is what `activate` returns.
 *
 * ── One resolved conflict with PART 5, recorded here rather than inferred ───
 * §2 says the finished item must be "of type `Made` (PART 5 › MD-03, the Item
 * type field)". PART 5's own open question 18 settles the item types as
 * "Product, Bundle, Service" — there is no Made type in the shipped item
 * model, and PART 5 is the authority on its own field. So a MADE ITEM IS A
 * PRODUCT THAT HAS A RECIPE: the finished-item picker offers stocked products,
 * and everything that asks "is this made?" asks whether a bill of materials
 * exists. A Bundle is deliberately excluded — owner ruling R7's kit is
 * assembled at sale from lines that already reference real items, which is a
 * different mechanism with its own deduction path.
 */
// ── Shapes ──────────────────────────────────────────────────────────────────
export const BomComponentSchema = z
    .object({
    id: z.uuid(),
    lineNo: z.number().int(),
    itemId: z.uuid(),
    itemCode: z.string(),
    itemName: z.string(),
    /** Field 8: the blue `Sub-assembly BOM` label — the component has a recipe of its own. */
    isSubAssembly: z.boolean(),
    /** Field 8 [decided at build time]: made a LINK, so the child recipe opens. */
    subAssemblyVersionId: z.uuid().nullable(),
    qty: z.string(),
    unitId: z.uuid(),
    unitSymbol: z.string(),
    /**
     * `N-4` — the decimals THIS LINE's unit allows. It follows `unitId`, not
     * the item's stock unit: `bom_components.unit_id` is per line, so a recipe
     * may call for grams of something the shelf holds in kilograms, and
     * `BR-MD-03-04d`'s "the LINE's unit" is the rule here too.
     */
    unitPrecision: UnitPrecisionSchema,
    scrapPct: z.string(),
    /** Field 12: `qty × (1 + scrap ÷ 100)`, 3 dp. */
    qtyWithScrap: z.string(),
    /** Field 13, from the costing service. Never typed (BR-MFG-01-12). */
    unitCost: z.string(),
    /** Field 14: `qty +scrap × unit cost`, 2 dp. */
    lineCost: z.string(),
    isOptional: z.boolean(),
})
    .strict();
export const BomOperationSchema = z
    .object({
    id: z.uuid(),
    /** Field 16: the row's POSITION. BR-MFG-01-10 — never typed. */
    seq: z.number().int(),
    name: z.string(),
    minutes: z.number().int(),
    workstation: z.string().nullable(),
    /**
     * BR-MFG-01-11's WARNING, computed per row: no other operation in the
     * tenant names this workstation string, so it is probably a typo and
     * capacity planning will treat it as its own machine. Never a block.
     */
    isLonelyWorkstation: z.boolean(),
})
    .strict();
export const BomVersionSummarySchema = z
    .object({
    id: z.uuid(),
    versionNo: z.number().int(),
    /** Field 4: the `v3` chip. */
    label: z.string(),
    status: BomStatusSchema,
    changeNote: z.string().nullable(),
    effectiveDate: z.string().nullable(),
    /** §7's hidden system status: BR-MFG-01-07 reads this before allowing a delete. */
    workOrderCount: z.number().int(),
})
    .strict();
const bomShape = z
    .object({
    id: z.uuid(),
    finishedItemId: z.uuid(),
    finishedItemCode: z.string(),
    finishedItemName: z.string(),
    versionNo: z.number().int(),
    label: z.string(),
    status: BomStatusSchema,
    uomId: z.uuid(),
    uomSymbol: z.string(),
    /** `N-4` — the decimals the batch unit allows (MFG-01 §5.1 field 5). */
    uomPrecision: UnitPrecisionSchema,
    effectiveDate: z.string().nullable(),
    changeNote: z.string().nullable(),
    /** BR-MFG-01-13: an Active version is read-only apart from `overheadPct`. */
    isEditable: z.boolean(),
    /** BR-MFG-01-07 / -18: a version a work order snapshotted is never deleted. */
    workOrderCount: z.number().int(),
    components: BomComponentSchema.array(),
    operations: BomOperationSchema.array(),
    /** Field 20: the routing card header's `Total time: 32 min`. */
    totalMinutes: z.number().int(),
    /** §9.4, via the shared service. The screen shows it; nothing here types it. */
    cost: CostBreakdownSchema,
    /** §8.3, newest first. Read by anyone who may open the screen. */
    versions: BomVersionSummarySchema.array(),
    createdAt: z.string(),
    updatedAt: z.string(),
})
    .strict();
/** §8.4 — parent recipes only. "You cannot look downward into a sub-assembly from here." */
export const WhereUsedRowSchema = z
    .object({
    bomVersionId: z.uuid(),
    parentName: z.string(),
    finishedSku: z.string(),
    label: z.string(),
    status: BomStatusSchema,
})
    .strict();
const ComponentInputSchema = z
    .object({
    itemId: z.uuid(),
    qty: QtyStringSchema,
    unitId: z.uuid(),
    scrapPct: MfgPctSchema.optional(),
    isOptional: z.boolean().optional(),
})
    .strict();
const OperationInputSchema = z
    .object({
    name: z.string().trim().min(1).max(80),
    /** Field 18: whole minutes, 1..9999. There is no seconds field. */
    minutes: z.number().int().min(1).max(9999),
    workstation: z.string().trim().max(80).nullable().optional(),
})
    .strict();
export const CreateBomRequestSchema = z
    .object({
    finishedItemId: z.uuid(),
    uomId: z.uuid(),
    effectiveDate: z.iso.date().nullable().optional(),
    overheadPct: MfgPctSchema.optional(),
    changeNote: z.string().max(200).nullable().optional(),
    components: ComponentInputSchema.array().max(200).optional(),
    operations: OperationInputSchema.array().max(100).optional(),
})
    .strict();
/**
 * Whole-list replacement for both children, which is what BR-MFG-01-10 asks
 * for: "Reordering must renumber every operation and must change nothing
 * else." A drag is a new order of the same rows, so it is one request.
 */
export const UpdateBomRequestSchema = z
    .object({
    uomId: z.uuid().optional(),
    effectiveDate: z.iso.date().nullable().optional(),
    /** The one field an ACTIVE version still accepts (BR-MFG-01-13). */
    overheadPct: MfgPctSchema.optional(),
    changeNote: z.string().max(200).nullable().optional(),
    components: ComponentInputSchema.array().max(200).optional(),
    operations: OperationInputSchema.array().max(100).optional(),
})
    .strict();
export const SetBomStatusRequestSchema = z
    .object({
    status: BomStatusSchema,
    /**
     * §8.5: the activate prompt "cannot be dismissed by clicking the
     * background — you must choose one of the two buttons", and its confirm
     * button is worded **Deactivate & activate**. The server refuses an
     * activation that would silently retire an incumbent unless the caller
     * says it saw the prompt, so the switch-over can never happen by accident
     * through the API either.
     */
    deactivateIncumbent: z.boolean().optional(),
})
    .strict();
/**
 * ── Why this one schema is declared and not inferred ────────────────────────
 * `BomSchema` answers 5 of this module's routes. Annotating it as
 * `z.ZodType<BomResponse>` writes the shape ONCE and lets every route
 * reference it by name, which keeps this module's emitted declaration a few
 * kilobytes instead of a hundred. Runtime validation is unchanged: the object
 * schema above is still what parses.
 */
export const BomSchema = bomShape;
const bomParams = z.object({ bomId: z.uuid() }).strict();
export const bomContract = c.router({
    list: {
        method: 'GET',
        path: '/api/manufacturing/boms',
        query: pagedQuery({
            finishedItemId: z.uuid().optional(),
            status: BomStatusSchema.optional(),
        }),
        responses: {
            200: pagedResponse('boms', BomSchema),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'Recipes (manufacturing.view), PAGED (NFR-42) — both filters in SQL.',
    },
    get: {
        method: 'GET',
        path: '/api/manufacturing/boms/:bomId',
        pathParams: bomParams,
        responses: {
            200: BomSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        // §8.3: version history is readable by ANYONE who may open the screen;
        // only changing which version is live needs the permission (BR-MFG-01-17).
        summary: 'One version: components, routing, cost panel, version history.',
    },
    create: {
        method: 'POST',
        path: '/api/manufacturing/boms',
        body: CreateBomRequestSchema,
        responses: {
            201: BomSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        // §15: an item with no recipe opens a blank Draft v1; an item that has
        // versions gets the next number.
        summary: 'A new DRAFT version (manufacturing.create).',
    },
    update: {
        method: 'PATCH',
        path: '/api/manufacturing/boms/:bomId',
        pathParams: bomParams,
        body: UpdateBomRequestSchema,
        responses: {
            200: BomSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        // On an ACTIVE version only overheadPct is accepted; anything else
        // answers BR-MFG-01-13. Loops (BR-MFG-01-04) are walked to any depth.
        summary: 'Edit a DRAFT (manufacturing.edit).',
    },
    remove: {
        method: 'DELETE',
        path: '/api/manufacturing/boms/:bomId',
        pathParams: bomParams,
        body: c.noBody(),
        responses: {
            204: c.noBody(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        // Only a Draft, and only when no work order references it
        // (BR-MFG-01-07 — "Version 2 was used by 4 work orders").
        summary: 'Delete a DRAFT (manufacturing.delete).',
    },
    setStatus: {
        method: 'POST',
        path: '/api/manufacturing/boms/:bomId/status',
        pathParams: bomParams,
        body: SetBomStatusRequestSchema,
        responses: {
            200: BomSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        // Activating deactivates the incumbent IN THE SAME STEP (BR-MFG-01-02)
        // and opens a new Draft one number higher (OQ-9.7-4).
        summary: 'Draft/Active/Inactive (manufacturing.approve).',
    },
    whereUsed: {
        method: 'GET',
        path: '/api/manufacturing/boms/where-used/:itemId',
        pathParams: z.object({ itemId: z.uuid() }).strict(),
        responses: {
            200: z.object({ rows: WhereUsedRowSchema.array() }).strict(),
            401: MessageSchema,
            403: MessageSchema,
        },
        // "If I change this part, which recipes break?" Parent recipes only,
        // upward only. Needs nothing beyond the read permission — looking is
        // harmless (§6's own note on the Where used icon).
        summary: '§8.4 Where used.',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=bom.js.map